import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, StatusPill } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "@/components/ui/tabs";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    apiListProjects,
    apiListTasks,
    apiTasksCalendar,
    apiCreateTask,
    apiUpdateTask,
    apiUpdateTaskStatus,
    apiSendTaskReminderNow,
    apiDeleteTask,
    apiGetTaskReminderEmails,
    apiSetTaskReminderEmails,
    formatApiError,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    ListTodo,
    Plus,
    Pencil,
    Trash2,
    Repeat,
    Calendar as CalendarIcon,
    Bell,
    Send,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
} from "lucide-react";

// Status vocabulary + Tailwind color palette. Kept next to the Select options
// so the palette stays in sync everywhere (list badges, calendar chips, dialog).
const STATUS_OPTIONS = ["Planned", "Started", "Completed", "Pending", "Missed", "Cancelled"];
const STATUS_COLOR = {
    Planned: "bg-[#DBEAF6] text-[#0072BC] border border-[#0072BC]/25",
    Started: "bg-emerald-100 text-emerald-800 border border-emerald-300",
    Completed: "bg-emerald-600 text-white border border-emerald-700",
    Pending: "bg-amber-100 text-amber-800 border border-amber-300",
    Missed: "bg-red-100 text-red-800 border border-red-300",
    Cancelled: "bg-gray-200 text-gray-700 border border-gray-300",
};
const CAL_STATUS_BG = {
    Planned: "bg-[#0072BC]",
    Started: "bg-emerald-600",
    Completed: "bg-emerald-800",
    Pending: "bg-amber-500",
    Missed: "bg-red-600",
    Cancelled: "bg-gray-400",
};

function canManage(user, project) {
    if (!user || !project) return false;
    if (user.role === "Super Admin" || user.role === "Admin") return true;
    if (user.role === "Project Manager" && project.project_manager_id === user.user_id) return true;
    return false;
}

export default function ProjectTasks() {
    const { project_id: projectId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("list");
    const [addOpen, setAddOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [remindersOpen, setRemindersOpen] = useState(false);

    const loadProjects = useCallback(async () => {
        try {
            setProjects(await apiListProjects());
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);

    const loadTasks = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const p = (await apiListProjects()).find((x) => x.project_id === projectId);
            setProject(p || null);
            const t = await apiListTasks(projectId);
            setTasks(t);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) {
            loadTasks();
        } else {
            setLoading(true);
            loadProjects().finally(() => setLoading(false));
        }
    }, [projectId, loadTasks, loadProjects]);

    // -------- Picker (no project selected) --------
    if (!projectId) {
        return (
            <Layout active="tasks">
                <PageHeader
                    title="Project tasks"
                    subtitle="Pick a project to see its task list and calendar."
                />
                {loading ? (
                    <div className="text-sm text-ntt-muted">Loading projects…</div>
                ) : projects.length === 0 ? (
                    <div className="p-10 text-center bg-white border border-ntt-border rounded-xl">
                        <div className="text-sm text-ntt-muted">No projects available.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tasks-project-picker">
                        {projects.map((p) => (
                            <Link
                                key={p.project_id}
                                to={`/tasks/${p.project_id}`}
                                data-testid={`tasks-project-card-${p.project_id}`}
                                className="p-4 bg-white border border-ntt-border rounded-xl hover:border-[#0072BC] hover:shadow-md transition-all"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <ListTodo className="h-4 w-4 text-[#0072BC]" />
                                    <div className="text-[13px] font-semibold text-[#070F26] truncate">{p.name}</div>
                                </div>
                                <div className="text-[11px] font-mono text-ntt-muted">{p.code}</div>
                                {p.customer_name && (
                                    <div className="text-[11px] text-ntt-muted mt-1 truncate">
                                        {p.customer_name}
                                    </div>
                                )}
                                <div className="mt-2">
                                    <StatusPill
                                        label={p.status || "Active"}
                                        tone={
                                            p.status === "On Hold"
                                                ? "amber"
                                                : p.status === "Completed"
                                                  ? "green"
                                                  : p.status === "Archived"
                                                    ? "gray"
                                                    : "blue"
                                        }
                                    />
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </Layout>
        );
    }

    const managed = canManage(user, project);

    // -------- Per-project view --------
    return (
        <Layout active="tasks">
            <PageHeader
                title={project ? `Tasks · ${project.name}` : "Tasks"}
                subtitle={
                    project ? (
                        <span className="flex items-center gap-2 text-[12px] text-ntt-muted">
                            <span className="font-mono">{project.code}</span>
                            {project.customer_name && <>·<span>{project.customer_name}</span></>}
                            {project.project_manager_name && (
                                <>· PM <span className="text-[#070F26]">{project.project_manager_name}</span></>
                            )}
                        </span>
                    ) : (
                        "…"
                    )
                }
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate("/tasks")}
                            data-testid="tasks-back-to-picker"
                            className="h-9"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1.5" /> All projects
                        </Button>
                        {managed && (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => setRemindersOpen(true)}
                                    data-testid="tasks-edit-reminders-btn"
                                    className="h-9"
                                >
                                    <Bell className="h-4 w-4 mr-1.5" /> Reminder emails
                                </Button>
                                <Button
                                    onClick={() => setAddOpen(true)}
                                    data-testid="tasks-add-btn"
                                    className="h-9 bg-[#0072BC] hover:bg-[#005a94] text-white"
                                >
                                    <Plus className="h-4 w-4 mr-1.5" /> Add task
                                </Button>
                            </>
                        )}
                    </div>
                }
            />

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="tasks-tabs">
                    <TabsTrigger value="list" data-testid="tasks-tab-list">
                        <ListTodo className="h-4 w-4 mr-1.5" /> List
                    </TabsTrigger>
                    <TabsTrigger value="calendar" data-testid="tasks-tab-calendar">
                        <CalendarIcon className="h-4 w-4 mr-1.5" /> Calendar
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="list" className="mt-4">
                    <TasksList
                        tasks={tasks}
                        loading={loading}
                        managed={managed}
                        projectId={projectId}
                        onEdit={setEditing}
                        onDelete={setDeleting}
                        onStatusChanged={loadTasks}
                    />
                </TabsContent>

                <TabsContent value="calendar" className="mt-4">
                    <TasksCalendar projectId={projectId} tasks={tasks} />
                </TabsContent>
            </Tabs>

            <TaskFormDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                mode="create"
                projectId={projectId}
                onDone={() => {
                    setAddOpen(false);
                    loadTasks();
                }}
            />
            <TaskFormDialog
                open={!!editing}
                onOpenChange={(v) => !v && setEditing(null)}
                mode="edit"
                task={editing}
                projectId={projectId}
                onDone={() => {
                    setEditing(null);
                    loadTasks();
                }}
            />
            <ReminderEmailsDialog
                open={remindersOpen}
                onOpenChange={setRemindersOpen}
                projectId={projectId}
            />

            <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
                <AlertDialogContent data-testid="tasks-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete task?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Permanently delete <b>{deleting?.name}</b>. Reminder log entries are kept.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="tasks-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async () => {
                                try {
                                    await apiDeleteTask(projectId, deleting.task_id);
                                    toast.success("Task deleted");
                                    setDeleting(null);
                                    loadTasks();
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                }
                            }}
                            data-testid="tasks-delete-confirm-btn"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}

function TasksList({ tasks, loading, managed, projectId, onEdit, onDelete, onStatusChanged }) {
    return (
        <div className="bg-white border border-ntt-border rounded-xl overflow-hidden" data-testid="tasks-list-table">
            <div className="grid grid-cols-[minmax(200px,2fr)_120px_140px_140px_140px_120px_220px] gap-4 px-5 py-3 border-b border-ntt-border bg-[#F4F6F9] text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                <div>Task</div>
                <div>Type</div>
                <div>Window</div>
                <div>Status</div>
                <div>Reminder</div>
                <div>Cadence</div>
                <div className="text-right">Actions</div>
            </div>
            {loading ? (
                <div className="p-8 text-center text-sm text-ntt-muted">Loading tasks…</div>
            ) : tasks.length === 0 ? (
                <div className="p-10 text-center text-sm text-ntt-muted italic">
                    No tasks yet for this project.
                </div>
            ) : (
                tasks.map((t) => (
                    <div
                        key={t.task_id}
                        data-testid={`task-row-${t.task_id}`}
                        className="grid grid-cols-[minmax(200px,2fr)_120px_140px_140px_140px_120px_220px] gap-4 px-5 py-3 border-b border-ntt-border last:border-0 items-center hover:bg-[#FBFCFE]"
                    >
                        <div>
                            <div className="text-[13px] font-medium text-[#070F26] truncate">{t.name}</div>
                            {t.description && (
                                <div className="text-[11px] text-ntt-muted truncate">{t.description}</div>
                            )}
                        </div>
                        <div className="text-[12px]">
                            {t.type === "recurring" ? (
                                <span className="inline-flex items-center gap-1 text-[#0072BC]">
                                    <Repeat className="h-3.5 w-3.5" /> {t.recurrence_frequency}
                                </span>
                            ) : (
                                <span className="text-ntt-muted">One-time</span>
                            )}
                        </div>
                        <div className="text-[12px] text-[#2B2F3A]">
                            {t.start_date}
                            <div className="text-[11px] text-ntt-muted">→ {t.due_date}</div>
                        </div>
                        <div>
                            <InlineStatusSelect task={t} projectId={projectId} onChanged={onStatusChanged} />
                        </div>
                        <div className="text-[12px] text-ntt-muted">{t.reminder_start_date || "—"}</div>
                        <div className="text-[12px] text-ntt-muted">{t.reminder_frequency || "—"}</div>
                        <div className="flex items-center justify-end gap-1">
                            {managed && (
                                <>
                                    <SendNowButton task={t} projectId={projectId} />
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onEdit(t)}
                                        data-testid={`task-edit-${t.task_id}`}
                                        className="h-8 text-[12px]"
                                    >
                                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onDelete(t)}
                                        data-testid={`task-delete-${t.task_id}`}
                                        className="h-8 text-[12px] text-red-600 hover:text-red-700"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

function InlineStatusSelect({ task, projectId, onChanged }) {
    const [busy, setBusy] = useState(false);
    async function change(v) {
        if (v === task.status) return;
        setBusy(true);
        try {
            const updated = await apiUpdateTaskStatus(projectId, task.task_id, v);
            onChanged?.(updated);
            toast.success(`Status: ${v}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }
    return (
        <Select value={task.status} onValueChange={change} disabled={busy}>
            <SelectTrigger
                data-testid={`task-status-inline-${task.task_id}`}
                className={`h-7 border-0 shadow-none px-2 text-[11.5px] font-medium rounded-full w-fit min-w-[92px] ${STATUS_COLOR[task.status] || STATUS_COLOR.Planned}`}
            >
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                    <SelectItem
                        key={s}
                        value={s}
                        data-testid={`task-status-inline-${task.task_id}-${s}`}
                    >
                        <span className={`inline-block h-2 w-2 rounded-full mr-2 ${(CAL_STATUS_BG[s] || "bg-gray-400")}`}></span>
                        {s}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function SendNowButton({ task, projectId }) {
    const [busy, setBusy] = useState(false);
    async function send() {
        if (!window.confirm(`Send reminder for "${task.name}" now to the full distribution list?`)) {
            return;
        }
        setBusy(true);
        try {
            const res = await apiSendTaskReminderNow(projectId, task.task_id);
            toast.success(`Reminder sent to ${res.recipients?.length || 0} recipient(s)`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }
    return (
        <Button
            variant="outline"
            size="sm"
            onClick={send}
            disabled={busy}
            data-testid={`task-send-now-${task.task_id}`}
            title="Send this task's reminder email immediately"
            className="h-8 text-[12px] text-[#0072BC] hover:bg-[#DBEAF6]/50"
        >
            <Send className="h-3.5 w-3.5 mr-1" /> Send now
        </Button>
    );
}

// ---------- Calendar (month grid) ----------
function TasksCalendar({ projectId, tasks }) {
    const today = new Date();
    const [month, setMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
    const [occurrences, setOccurrences] = useState([]);
    const [loading, setLoading] = useState(false);

    const range = useMemo(() => {
        const first = new Date(month.getFullYear(), month.getMonth(), 1);
        const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
        const iso = (d) => d.toISOString().slice(0, 10);
        return { from: iso(first), to: iso(last), first, last };
    }, [month]);

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        apiTasksCalendar(projectId, range.from, range.to)
            .then((data) => {
                if (!cancel) setOccurrences(data.occurrences || []);
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => !cancel && setLoading(false));
        return () => {
            cancel = true;
        };
    }, [projectId, range.from, range.to, tasks.length]);

    // Group by day
    const byDay = useMemo(() => {
        const m = new Map();
        for (const o of occurrences) {
            (m.get(o.occurrence_start) || m.set(o.occurrence_start, []).get(o.occurrence_start)).push(o);
        }
        return m;
    }, [occurrences]);

    // Calendar grid: leading blanks + days
    const firstDay = range.first.getDay(); // 0=Sun
    const daysInMonth = range.last.getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const dt = new Date(month.getFullYear(), month.getMonth(), d);
        cells.push(dt);
    }
    // Pad to 42 cells (6 weeks)
    while (cells.length < 42) cells.push(null);

    const monthLabel = month.toLocaleString("en-US", { month: "long", year: "numeric" });

    return (
        <div
            className="bg-white border border-ntt-border rounded-xl overflow-hidden"
            data-testid="tasks-calendar"
        >
            <div className="flex items-center justify-between px-4 py-3 border-b border-ntt-border">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                    data-testid="tasks-cal-prev"
                    className="h-8"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-[14px] font-semibold text-[#070F26]" data-testid="tasks-cal-month">
                    {monthLabel}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                    data-testid="tasks-cal-next"
                    className="h-8"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>
            <div className="grid grid-cols-7 border-b border-ntt-border text-[11px] font-medium uppercase tracking-wider text-ntt-muted">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="px-2 py-2 text-center">
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {cells.map((dt, i) => {
                    if (!dt) return <div key={`empty-${i}`} className="h-24 bg-[#FBFCFE] border-b border-r border-ntt-border" />;
                    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
                    const events = byDay.get(iso) || [];
                    const isToday = iso === new Date().toISOString().slice(0, 10);
                    return (
                        <div
                            key={iso}
                            data-testid={`tasks-cal-cell-${iso}`}
                            className={`h-24 border-b border-r border-ntt-border p-1.5 overflow-hidden ${isToday ? "bg-blue-50/60" : ""}`}
                        >
                            <div className={`text-[11px] mb-1 ${isToday ? "font-bold text-[#0072BC]" : "text-ntt-muted"}`}>
                                {dt.getDate()}
                            </div>
                            <div className="space-y-1">
                                {events.slice(0, 3).map((e, ix) => (
                                    <div
                                        key={`${e.task_id || e.name}-${e.occurrence_start || ix}`}
                                        title={`${e.name} · ${e.status} · ${e.occurrence_start} → ${e.occurrence_due}`}
                                        className={`text-[10.5px] px-1.5 py-0.5 rounded truncate text-white ${CAL_STATUS_BG[e.status] || "bg-[#0072BC]"}`}
                                    >
                                        {e.name}
                                    </div>
                                ))}
                                {events.length > 3 && (
                                    <div className="text-[10px] text-ntt-muted">+ {events.length - 3} more</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {loading && (
                <div className="p-3 text-center text-[11px] text-ntt-muted">Loading calendar…</div>
            )}
        </div>
    );
}

// ---------- Task create/edit dialog ----------
function TaskFormDialog({ open, onOpenChange, mode, task, projectId, onDone }) {
    const [form, setForm] = useState({
        name: "",
        description: "",
        type: "one_time",
        start_date: "",
        due_date: "",
        recurrence_frequency: "weekly",
        reminder_start_date: "",
        reminder_frequency: "__none__",
        status: "Planned",
    });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                name: task?.name || "",
                description: task?.description || "",
                type: task?.type || "one_time",
                start_date: task?.start_date || "",
                due_date: task?.due_date || "",
                recurrence_frequency: task?.recurrence_frequency || "weekly",
                reminder_start_date: task?.reminder_start_date || "",
                reminder_frequency: task?.reminder_frequency || "__none__",
                status: task?.status || "Planned",
            });
            setError("");
        }
    }, [open, task]);

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit(e) {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || null,
                type: form.type,
                start_date: form.start_date,
                due_date: form.due_date,
                recurrence_frequency: form.type === "recurring" ? form.recurrence_frequency : null,
                reminder_start_date: form.reminder_start_date || null,
                reminder_frequency:
                    form.reminder_frequency === "__none__" ? null : form.reminder_frequency,
                status: form.status,
            };
            if (mode === "create") {
                await apiCreateTask(projectId, payload);
                toast.success("Task created");
            } else {
                await apiUpdateTask(projectId, task.task_id, payload);
                toast.success("Task updated");
            }
            onDone?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[620px] max-h-[90vh] overflow-y-auto"
                data-testid="task-dialog"
            >
                <DialogHeader>
                    <DialogTitle>{mode === "create" ? "Add task" : "Edit task"}</DialogTitle>
                    <DialogDescription>
                        Tasks show on the project's calendar and can trigger scheduled email reminders.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="task-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Name</Label>
                            <Input
                                required
                                value={form.name}
                                onChange={(e) => set("name", e.target.value)}
                                placeholder="Prepare kickoff deck"
                                data-testid="task-name-input"
                                className="h-10"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Description (optional)
                            </Label>
                            <textarea
                                value={form.description}
                                onChange={(e) => set("description", e.target.value)}
                                data-testid="task-description-input"
                                className="w-full min-h-[70px] rounded-md border border-ntt-border p-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0072BC]/40"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Type</Label>
                            <Select value={form.type} onValueChange={(v) => set("type", v)}>
                                <SelectTrigger className="h-10" data-testid="task-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="one_time" data-testid="task-type-one_time">One-time</SelectItem>
                                    <SelectItem value="recurring" data-testid="task-type-recurring">Recurring</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Status</Label>
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger className="h-10" data-testid="task-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {STATUS_OPTIONS.map((s) => (
                                        <SelectItem key={s} value={s} data-testid={`task-status-${s}`}>
                                            <span className={`inline-block h-2 w-2 rounded-full mr-2 ${(CAL_STATUS_BG[s] || "bg-gray-400")}`}></span>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Start date</Label>
                            <Input
                                required
                                type="date"
                                value={form.start_date}
                                onChange={(e) => set("start_date", e.target.value)}
                                data-testid="task-start-date-input"
                                className="h-10"
                            />
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">Due date</Label>
                            <Input
                                required
                                type="date"
                                value={form.due_date}
                                onChange={(e) => set("due_date", e.target.value)}
                                data-testid="task-due-date-input"
                                className="h-10"
                            />
                        </div>
                        {form.type === "recurring" && (
                            <div className="col-span-2">
                                <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                    Recurrence frequency
                                </Label>
                                <Select
                                    value={form.recurrence_frequency}
                                    onValueChange={(v) => set("recurrence_frequency", v)}
                                >
                                    <SelectTrigger className="h-10" data-testid="task-recurrence-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {["daily", "weekly", "monthly", "quarterly"].map((s) => (
                                            <SelectItem key={s} value={s} data-testid={`task-recurrence-${s}`}>
                                                {s}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="col-span-2 mt-1 border-t border-ntt-border pt-3">
                            <div className="text-[11px] font-medium uppercase tracking-wider text-ntt-muted mb-2 flex items-center gap-1.5">
                                <Bell className="h-3 w-3" /> Email reminder (optional)
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                        Reminder start date
                                    </Label>
                                    <Input
                                        type="date"
                                        value={form.reminder_start_date}
                                        onChange={(e) => set("reminder_start_date", e.target.value)}
                                        data-testid="task-reminder-start-input"
                                        className="h-10"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                        Reminder frequency
                                    </Label>
                                    <Select
                                        value={form.reminder_frequency}
                                        onValueChange={(v) => set("reminder_frequency", v)}
                                    >
                                        <SelectTrigger className="h-10" data-testid="task-reminder-freq-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__" data-testid="task-reminder-freq-none">
                                                No reminders
                                            </SelectItem>
                                            {["daily", "weekly", "monthly"].map((s) => (
                                                <SelectItem key={s} value={s} data-testid={`task-reminder-freq-${s}`}>
                                                    {s}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="text-[11px] text-ntt-muted mt-2">
                                Reminders stop automatically when the due date has passed.
                            </div>
                        </div>
                    </div>
                    {error && (
                        <div
                            data-testid="task-form-error"
                            className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                        >
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="task-cancel-btn"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="task-save-btn"
                            className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                        >
                            {saving ? "Saving…" : mode === "create" ? "Create" : "Save"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Reminder emails dialog ----------
function ReminderEmailsDialog({ open, onOpenChange, projectId }) {
    const [emails, setEmails] = useState([]);
    const [draft, setDraft] = useState("");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setDraft("");
            setError("");
            apiGetTaskReminderEmails(projectId).then(setEmails).catch((err) => toast.error(formatApiError(err)));
        }
    }, [open, projectId]);

    function add(raw) {
        const e = (raw || "").trim().toLowerCase();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
            setError(`"${e}" is not a valid email`);
            return;
        }
        if (!emails.includes(e)) setEmails([...emails, e]);
        setDraft("");
        setError("");
    }
    function remove(e) {
        setEmails(emails.filter((x) => x !== e));
    }

    async function save() {
        setSaving(true);
        try {
            const fresh = await apiSetTaskReminderEmails(projectId, emails);
            setEmails(fresh);
            toast.success("Reminder email list saved");
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[520px]" data-testid="tasks-reminders-dialog">
                <DialogHeader>
                    <DialogTitle>Task reminder email list</DialogTitle>
                    <DialogDescription>
                        In addition to the project's roster members and its Project Manager, reminders
                        will also go to every email in this list.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="flex flex-wrap gap-1.5" data-testid="tasks-reminder-chips">
                        {emails.length === 0 && (
                            <span className="text-[12px] text-ntt-muted italic">None yet.</span>
                        )}
                        {emails.map((e) => (
                            <span
                                key={e}
                                data-testid={`tasks-reminder-chip-${e}`}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-[12px] bg-[#DBEAF6] text-[#0072BC]"
                            >
                                {e}
                                <button
                                    type="button"
                                    onClick={() => remove(e)}
                                    className="hover:text-red-600"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input
                            type="email"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === ",") {
                                    e.preventDefault();
                                    add(draft);
                                }
                            }}
                            onBlur={() => draft && add(draft)}
                            placeholder="Add an email and press Enter"
                            data-testid="tasks-reminder-input"
                            className="h-9"
                        />
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => add(draft)}
                            data-testid="tasks-reminder-add-btn"
                            className="h-9"
                        >
                            Add
                        </Button>
                    </div>
                    {error && (
                        <div className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                            <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="tasks-reminders-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        data-testid="tasks-reminders-save"
                        className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
